import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { HardHat, Info, ChevronDown, Search, X } from "lucide-react";
import FrsTable from "../components/FrsTable";
import { api } from "../lib/api";

/**
 * Job picker — a tap opens a search box over the full scrollable job list, so a
 * long project list is reachable by job number or name on a phone.
 */
const JobPicker = ({ projects, value, onChange }) => {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const current = projects.find((p) => p.id === value);
  const label = current
    ? `${current.job_number ? `${current.job_number} · ` : ""}${current.project_name}`
    : "Select a job…";
  const needle = q.trim().toLowerCase();
  const shown = needle
    ? projects.filter((p) =>
        `${p.job_number || ""} ${p.project_name || ""} ${p.address || ""}`.toLowerCase().includes(needle))
    : projects;

  return (
    <>
      <button
        type="button"
        onClick={() => { setQ(""); setOpen(true); }}
        data-testid="install-project-select"
        className="h-9 flex-1 min-w-0 flex items-center gap-1 text-sm border border-border rounded-lg px-2 bg-background text-foreground text-left hover:bg-muted/60 transition-colors"
      >
        <span className="truncate" data-testid="install-project-label">{label}</span>
        <ChevronDown className="w-4 h-4 ml-auto shrink-0 text-muted-foreground/70" />
      </button>

      {open && (
        <div
          className="fixed inset-0 z-[70] flex items-start justify-center p-3 sm:pt-24"
          onClick={(e) => { if (e.target === e.currentTarget) setOpen(false); }}
          data-testid="install-project-picker"
        >
          <div className="absolute inset-0 bg-black/40" onClick={() => setOpen(false)} />
          <div className="relative w-full sm:w-[440px] max-h-[80vh] flex flex-col rounded-2xl bg-card border border-border shadow-2xl overflow-hidden">
            <div className="flex items-center gap-2 p-2 border-b border-border">
              <Search className="w-4 h-4 shrink-0 text-muted-foreground/70" />
              <input
                autoFocus
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search..."
                data-testid="install-project-search"
                className="h-9 flex-1 min-w-0 text-sm outline-none bg-transparent"
              />
              <button
                type="button"
                onClick={() => setOpen(false)}
                aria-label="Close job list"
                data-testid="install-project-close"
                className="h-9 w-9 grid place-items-center rounded-lg text-muted-foreground active:bg-muted"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="overflow-y-auto overscroll-contain">
              {shown.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => { onChange(p.id); setOpen(false); }}
                  data-testid={`install-project-option-${p.id}`}
                  className={`w-full text-left px-3 py-3 border-b border-border active:bg-blue-50 ${
                    p.id === value ? "bg-blue-50/70" : ""}`}
                >
                  <div className="text-sm text-foreground truncate">{p.project_name}</div>
                  <div className="text-[11px] text-muted-foreground truncate">
                    {p.job_number ? <span className="font-mono text-blue-700">{p.job_number}</span> : null}
                    {p.job_number && p.address ? " · " : ""}
                    {p.address || ""}
                  </div>
                </button>
              ))}
              {shown.length === 0 && (
                <div className="px-3 py-6 text-sm text-muted-foreground text-center" data-testid="install-project-empty">
                  No job matches “{q}”
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

/**
 * Install tab — the installer's field view of the FRS.
 * Same grid, same data, same filters as the Project FRS tab; adds the
 * room × scope completion card. Built to work on a phone or tablet.
 */
export default function InstallPage() {
  const [projects, setProjects] = useState([]);
  const [params, setParams] = useSearchParams();
  const projectId = params.get("project") || "";
  const mobId = params.get("mob") || "";
  const [focus, setFocus] = useState(null);   // { ids, label }
  // FT-2 §1/§2 — how this view opens. Never the first project in the list: either the
  // installer's dispatch card for today / this week, or the last job they worked.
  const [landing, setLanding] = useState(null);
  const [dispatchFilter, setDispatchFilter] = useState(null);
  const [showTip, setShowTip] = useState(false);
  const [installer, setInstaller] = useState(
    () => localStorage.getItem("fieldops-installer-name") || "",
  );

  useEffect(() => {
    let alive = true;
    Promise.all([
      api.get("/projects"),
      api.get("/install/landing").then((r) => r.data).catch(() => ({ mode: "last_project" })),
    ]).then(([{ data }, land]) => {
      if (!alive) return;
      const active = (data || []).filter((p) => (p.status || "active") !== "archived");
      setProjects(active);
      setLanding(land);
      if (projectId || mobId) return;
      const remembered = localStorage.getItem("fieldops-install-last-project") || "";
      if (land.mode === "dispatch" && land.project_id
          && active.some((p) => p.id === land.project_id)) {
        setParams({ project: land.project_id, ...(land.mob_id ? { mob: land.mob_id } : {}) },
                  { replace: true });
        setDispatchFilter({ building: land.building, floor: land.floor, area: land.area,
                            label: land.label, date: land.assigned_date });
      } else if (remembered && active.some((p) => p.id === remembered)) {
        setParams({ project: remembered }, { replace: true });
      }
    }).catch(() => { if (alive) setProjects([]); });
    return () => { alive = false; };
    // eslint-disable-next-line
  }, []);

  // The remembered job is per device and only ever what was actually opened.
  useEffect(() => {
    if (projectId) localStorage.setItem("fieldops-install-last-project", projectId);
  }, [projectId]);

  // Name comes from the signed-in user and is then remembered per device.
  // Falls back to the JWT's email when /auth/me is unavailable (preview).
  useEffect(() => {
    if (installer) return;
    let alive = true;
    const remember = (name) => {
      if (!name || !alive) return;
      setInstaller(name);
      localStorage.setItem("fieldops-installer-name", name);
    };
    const token = localStorage.getItem("auth_token");
    if (!token) return;
    const fromToken = (() => {
      try {
        const p = JSON.parse(atob(token.split(".")[1] || ""));
        return p.name || p.email || p.sub || "";
      } catch { return ""; }
    })();
    api.get("/auth/me")
      .then(({ data }) => remember(data?.name || data?.email || fromToken))
      .catch(() => remember(fromToken));
    return () => { alive = false; };
    // eslint-disable-next-line
  }, []);

  // Cluster 9 — arriving from a dispatch card: pull that card's rows so the
  // grid opens on exactly the dispatched scope / rooms.
  useEffect(() => {
    if (!mobId) { setFocus(null); return; }
    let alive = true;
    api.get("/mobilizations", { params: projectId ? { project_id: projectId } : {} })
      .then(({ data }) => {
        if (!alive) return;
        const mob = (data || []).find((m) => m.id === mobId);
        if (!mob) { setFocus(null); return; }
        setFocus({ ids: mob.frs_item_ids || [], label: mob.title || mob.scope_type || "" });
        setDispatchFilter({ building: String(mob.building || ""), floor: String(mob.floor || ""),
                            area: String(mob.area || ""),
                            label: mob.title || mob.scope_type || "",
                            date: mob.scheduled_start_date || "" });
        if (!projectId && mob.project_id) setParams({ project: mob.project_id, mob: mobId }, { replace: true });
      })
      .catch(() => { if (alive) setFocus(null); });
    return () => { alive = false; };
    // eslint-disable-next-line
  }, [mobId, projectId]);

  return (
    <div className="px-2 py-2 sm:p-4 space-y-2" data-testid="install-page">
      {/* One compact row — the quantity grid needs the vertical space. */}
      <div className="wh-house flex items-center gap-1.5 sm:gap-2 rounded-xl border border-border bg-card px-2 py-1.5 shadow-sm">
        <HardHat className="w-5 h-5 shrink-0 text-orange-600" />
        <h1 className="sr-only">Install</h1>
        <JobPicker
          projects={projects}
          value={projectId}
          onChange={(id) => setParams({ project: id })}
        />
        <input
          value={installer}
          onChange={(e) => {
            setInstaller(e.target.value);
            localStorage.setItem("fieldops-installer-name", e.target.value);
          }}
          placeholder="Your name"
          data-testid="install-installer-name"
          className="h-9 w-28 sm:w-40 shrink-0 text-sm border border-border rounded-lg px-2 bg-background text-foreground"
        />
        <button
          type="button"
          onClick={() => setShowTip((v) => !v)}
          aria-label="How this screen works"
          data-testid="install-help-toggle"
          className="h-9 w-9 shrink-0 grid place-items-center rounded-lg border border-border bg-background text-amber-600 hover:bg-muted transition-colors"
        >
          <Info className="w-4 h-4" />
        </button>
      </div>

      {showTip && (
        <div className="flex items-start gap-2 text-[12px] text-muted-foreground bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 dark:bg-amber-950/40 dark:border-amber-800"
             data-testid="install-tip">
          <Info className="w-4 h-4 shrink-0 mt-0.5 text-amber-600" />
          Tap a quantity cell to complete that room for that scope (photos are optional).
          Tap a product header for its full details.
        </div>
      )}

      {projectId ? (
        <FrsTable
          key={`${projectId}-${mobId}`}
          projectId={projectId}
          installMode
          installerName={installer}
          focusItemIds={mobId ? (focus?.ids || []) : null}
          focusLabel={focus?.label || ""}
          dispatchFilter={dispatchFilter}
          onClearDispatch={() => setDispatchFilter(null)}
        />
      ) : (
        <div className="text-sm text-muted-foreground border border-dashed border-border rounded-lg p-6 text-center"
             data-testid="install-no-project">
          {landing?.mode === "dispatch"
            ? `${landing?.reason || "Nothing is dispatched to you"} — search for a job by number or name above.`
            : "Search for a job by number or name above to see its rooms."}
        </div>
      )}
    </div>
  );
}
